// Per-plan (B/C/D/S) HUD phase data for the branch stubs.
//
// data/plan-branches/branchPhases.json is GENERATED by
// `npx tsx scripts/exportSidePanelTabs.ts --site-json` from the
// side-data-plan-* ground-truth tabs; it carries {date, hudMetrics}
// phases from the 2029.1 branch point onward. Everything else a
// PlanAPhaseData phase carries (datacenters, wakeup, compute, ...) is
// cloned from the last shared Plan A phase before the branch — the
// no-deal branches share Plan A's history up to the deal window.

import branchPhasesJson from "@/data/plan-branches/branchPhases.json"
import type { HudMetrics, PlanAPhaseData } from "@/data/plan-a/planATypes"

export type BranchPlanId = "B" | "C" | "D" | "S"

type BranchPhase = {
  date: string
  employmentRatePct?: number
  safetyTaxMultiplier?: number
  totalSlowdownYears?: number
  worldComputeH100e?: number
  humanCogWorkers?: number
  humanPhysWorkers?: number
  aiCopies?: number
  aiSpeedTokS?: number
  hudMetrics: HudMetrics
}

const BRANCH_PHASES = branchPhasesJson as unknown as Record<
  BranchPlanId,
  BranchPhase[]
>

export const isBranchPlanId = (plan: string | null): plan is BranchPlanId =>
  plan === "B" || plan === "C" || plan === "D" || plan === "S"

// Cache per (planAData identity, plan) — planAData is stable per page load.
const cache = new WeakMap<PlanAPhaseData[], Map<BranchPlanId, PlanAPhaseData[]>>()

export function buildBranchPlaybookData(
  planAData: PlanAPhaseData[],
  plan: BranchPlanId
): PlanAPhaseData[] {
  const branchPhases = BRANCH_PHASES[plan]
  if (!branchPhases || branchPhases.length === 0) return planAData

  let byPlan = cache.get(planAData)
  if (!byPlan) {
    byPlan = new Map()
    cache.set(planAData, byPlan)
  }
  const cached = byPlan.get(plan)
  if (cached) return cached

  const firstBranchTime = new Date(branchPhases[0].date).getTime()
  const sharedHistory = planAData.filter(
    (phase) => new Date(phase.date).getTime() < firstBranchTime
  )
  const template = sharedHistory[sharedHistory.length - 1] ?? planAData[0]
  const built: PlanAPhaseData[] = [
    ...sharedHistory,
    ...branchPhases.map((branchPhase) => ({
      ...template,
      date: branchPhase.date,
      phase: branchPhase.date.slice(0, 4),
      hudMetrics: { ...template.hudMetrics, ...branchPhase.hudMetrics },
      employmentRatePct: branchPhase.employmentRatePct,
      safetyTaxMultiplier: branchPhase.safetyTaxMultiplier,
      totalSlowdownYears: branchPhase.totalSlowdownYears,
      worldComputeH100e: branchPhase.worldComputeH100e,
      humanCogWorkers: branchPhase.humanCogWorkers,
      humanPhysWorkers: branchPhase.humanPhysWorkers,
      aiCopies: branchPhase.aiCopies,
      aiSpeedTokS: branchPhase.aiSpeedTokS,
      // Plan-A-specific criteria narratives don't apply on the branches.
      hudMetricCriteria: undefined,
    })),
  ]
  byPlan.set(plan, built)
  return built
}
